import React from "react";
import { AspectRatio, Box, Flex } from "@chakra-ui/react";
import { BookDemoButton } from "components/event-tracking/book-demo-button";
import { Section } from "components/section";
import { DecorativeBackgroundImage } from "components/images";
import { TextTitle } from "ui/text";
import { LottieWithSafariVideo } from "ui/lottie";
import { BlurFadeIn } from "ui/animations";
import { remCalc } from "theme/utils";
import { desktopOnlyDisplay, mobileOnlyDisplay } from "utils";
import auraImage from "../../../../public/assets/home/hero-aura.png";
import animationData from "./animation.json";

const lottieMp4 = "https://cdn.sanity.io/files/pwmfmi47/production/29fbe59c77f64918a979a95b0dd10967dbf55dc1.mp4";
const lottieMp4Mobile = "https://cdn.sanity.io/files/pwmfmi47/production/10984188c83ccc2ece7181af2a0982d65a376055.mp4";
const lottieWebmMobile =
  "https://cdn.sanity.io/files/pwmfmi47/production/23f16c31a24c7ff1294603ecb297d49eabd9b163.webm";

const desktopLottieProps = {
  display: desktopOnlyDisplay("inline-block"),
  // Center the animation, its weighted heavier on the right
  mr: { base: remCalc(20), md: remCalc(36), lg: remCalc(52) },
};

type HomeHeroProps = {
  title: string;
  description: string;
};

export function HomeHero({ title, description }: HomeHeroProps) {
  return (
    <Section bg="new.blue.50" themeBgColor="new.blue.50" py={0} pos="relative" overflow="hidden">
      <Box>
        <Box pos="relative" overflow="visible" pt={{ base: "big", md: "biggest" }}>
          <Box
            // Ensure the text sits above the aura bg image
            pos="relative"
            zIndex={10}
          >
            <BlurFadeIn duration={0.8}>
              <TextTitle
                size="2xl"
                align="center"
                title={title}
                titleAs="h1"
                maxW="780px"
                description={description}
                descriptionAs="h2"
                actions={
                  <Box className="animate-actions">
                    <BookDemoButton pulse="light" />
                  </Box>
                }
              />
            </BlurFadeIn>
          </Box>

          <BlurFadeIn delay={0.3}>
            <Flex
              pos="relative"
              overflow="visible"
              py={{ base: "tiny", md: "biggest" }}
              pointerEvents="none"
              userSelect="none"
            >
              <Flex
                pos="absolute"
                inset={{ base: "-240px", md: 0 }}
                align="center"
                justify="center"
                // Center the aura vertically with the lottie
                mb={{ base: remCalc(20), md: remCalc(36), lg: remCalc(58) }}
              >
                <AspectRatio ratio={1 / 1} w={{ base: "100%", md: "76%" }} pos="relative">
                  <DecorativeBackgroundImage priority src={auraImage} loading="eager" />
                </AspectRatio>
              </Flex>

              <LottieWithSafariVideo
                isVideoTransparent
                viewportAmount={0.1}
                minDelay={1.3}
                ratio={1334 / 676}
                animationData={animationData}
                videoSrc={lottieMp4}
                placeholderBgColor="transparent"
                containerProps={desktopLottieProps}
                staticContainerProps={desktopLottieProps}
              />

              {/* Use mp4 on mobile screens, lottie was lagging in chrome */}
              <AspectRatio
                display={mobileOnlyDisplay("inline-block")}
                ratio={430 / 667}
                w="100%"
                borderRadius={{ base: "small", md: "bigger" }}
                maxW="mediaMaxW.mobile"
                mx="auto"
                overflow="hidden"
              >
                <video autoPlay loop muted playsInline width="100%" height="100%" preload="metadata">
                  <source src={lottieMp4Mobile} type="video/mp4;codecs=hvc1" />
                  <source src={lottieWebmMobile} type="video/webm" />
                </video>
              </AspectRatio>
            </Flex>
          </BlurFadeIn>
        </Box>
      </Box>
    </Section>
  );
}
